import BaseProduct from './BaseProduct';
import { ProductId } from '../types';
import store, { getLayerOpacity } from '../state';
import { getJetStreamWindGridUrl } from '../config';
import ManifestTimelineHelper from '../services/ManifestTimelineHelper';
import CanvasWindRenderer from '../utils/CanvasWindRenderer';
import maplibregl from 'maplibre-gl';
import LayerManager from './LayerManager';

export class JetStreamLayer extends BaseProduct {
  public readonly id: ProductId = 'jet_stream';
  private lastModel: string = '';
  private windRenderer: CanvasWindRenderer;
  private unsubscribeStore: (() => void) | null = null;

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);
    this.windRenderer = new CanvasWindRenderer(map, 'jet-stream-wind-canvas-source', 'jet-stream-wind-canvas-layer', 'jet_stream');
  }

  protected onActivate(): void {
    if (!this.unsubscribeStore) {
      let lastWinds = store.getState().showJetStreamWinds;
      let lastMode = store.getState().jetStreamDisplayMode;
      let lastRaster = store.getState().showJetStreamRaster;
      let lastVis = store.getState().jetStreamParticlesVisible;
      this.unsubscribeStore = store.subscribe((newState) => {
        if (!this.active) return;
        if (
          newState.showJetStreamWinds !== lastWinds ||
          newState.jetStreamDisplayMode !== lastMode ||
          newState.showJetStreamRaster !== lastRaster ||
          newState.jetStreamParticlesVisible !== lastVis
        ) {
          lastWinds = newState.showJetStreamWinds;
          lastMode = newState.jetStreamDisplayMode;
          lastRaster = newState.showJetStreamRaster;
          lastVis = newState.jetStreamParticlesVisible;
          this.refresh();
        }
      });
    }
    this.refresh();
  }

  protected onDeactivate(): void {
    this.layerManager.removeBufferedRasterLayer(this.id);
    this.layerManager.removeLayer(this.id);
    this.windRenderer.clear();
    this.lastModel = '';
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public renderInterpolated(targetTimeMs: number): void {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const showRaster = state.jetStreamDisplayMode !== 'particles' && state.showJetStreamRaster !== false;
    if (!showRaster) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const rawModel = state.selectedModels[this.id];
    const model = (rawModel && rawModel !== 'blend') ? rawModel : 'gfs';

    this.updateStandardInterpolatedRaster({
      layerId: this.id,
      product: this.id,
      model,
      targetTime: targetTimeMs,
      opacity,
      minzoom: 0,
      maxzoom: 5
    });
  }

  public refresh() {
    if (!this.active) return;
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const opacity = getLayerOpacity(this.id, 1.0);
    const current = state.currentTimeStep;
    if (!current) return;

    const rawModel = state.selectedModels[this.id];
    const model = (rawModel && rawModel !== 'blend') ? rawModel : 'gfs';

    // Cleanly purge previous MapLibre raster source when switching models
    if (this.lastModel && this.lastModel !== model) {
      this.layerManager.removeBufferedRasterLayer(this.id);
      this.layerManager.removeLayer(this.id);
    }
    this.lastModel = model;

    const clampedFrame = ManifestTimelineHelper.getClampedFrame(manifest, this.id, model, current);
    if (!clampedFrame) {
      this.layerManager.removeBufferedRasterLayer(this.id);
      this.layerManager.removeLayer(this.id);
      this.windRenderer.clear();
      return;
    }

    const dateStr = clampedFrame.dateStr;
    const hourStr = clampedFrame.hourStr.replace(/^Z/, '');

    const showRaster = state.jetStreamDisplayMode !== 'particles' && state.showJetStreamRaster !== false;
    const showParticles = state.jetStreamDisplayMode !== 'raster' && state.showJetStreamWinds !== false && state.jetStreamParticlesVisible !== false;

    // 1. Update raster tiles
    if (showRaster) {
      this.updateStandardInterpolatedRaster({
        layerId: this.id,
        product: this.id,
        model,
        targetTime: current,
        opacity,
        minzoom: 0,
        maxzoom: 5
      });
    } else {
      this.layerManager.removeBufferedRasterLayer(this.id);
      this.layerManager.removeLayer(this.id);
    }

    // 2. Update animated wind stream particles if enabled
    if (showParticles) {
      const gridUrl = getJetStreamWindGridUrl(manifest, model, dateStr, hourStr);
      this.windRenderer.setGridUrl(gridUrl, opacity);
    } else {
      this.windRenderer.clear();
    }
  }
}

export default JetStreamLayer;
